import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  authLogin,
  authRegister,
  selectAuthStatus,
  selectAuthError,
} from "../content/auth";

export default function LoginModal({
  open,
  onClose,
  mode: initialMode = "login",
  onSuccess,
}) {
  // ✅ Hooks must be declared unconditionally
  const [mode, setMode] = useState(initialMode);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [nick, setNick] = useState("");
  const [description, setDescription] = useState("");

  const dispatch = useDispatch();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const loading = status === "loading";

  // Keep local mode in sync if parent changes initialMode
  useEffect(() => setMode(initialMode), [initialMode]);

  // Lock body scroll and add ESC/backdrop handlers while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const action =
      mode === "register"
        ? authRegister({ login, password, nick, description })
        : authLogin({ login, password });

    const res = await dispatch(action);
    if (authLogin.fulfilled.match(res) || authRegister.fulfilled.match(res)) {
      onSuccess?.(res.payload.user);
      onClose?.();
    }
  };

  if (!open) return null; // ← keeps hooks safe and avoids conditional calls
  // Render into body to avoid parent stacking/overflow issues
  return ReactDOM.createPortal(
    <div
      className="wal-modal-overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // close on backdrop click only (ignore clicks inside modal)
        if (e.target.classList.contains("wal-modal-overlay")) onClose?.();
      }}
    >
      <div className="wal-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h4 className="wal-title">
          {mode === "login" ? "Sign in to comment" : "Create an account"}
        </h4>

        <form onSubmit={handleSubmit}>
          <input
            className="wal-input"
            placeholder="Login"
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            autoFocus
          />
          <input
            className="wal-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {mode === "register" && (
            <>
              <input
                className="wal-input"
                placeholder="Nickname"
                value={nick}
                onChange={(e) => setNick(e.target.value)}
              />
              <input
                className="wal-input"
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </>
          )}

          {error && <p className="wal-error">{String(error)}</p>}

          <div className="wal-actions">
            <button className="wal-btn wal-btn-primary" type="submit" disabled={loading}>
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
            <button type="button" className="wal-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>

        <div className="wal-footer">
          {mode === "login" ? (
            <>
              Don’t have an account?{" "}
              <button type="button" className="wal-link" onClick={() => setMode("register")}>
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button type="button" className="wal-link" onClick={() => setMode("login")}>
                Sign in
              </button>
            </>
          )}
        </div>
      </div>

      {/* Inline styles scoped to this component */}
      <style>{`
        .wal-modal-overlay {
          position: fixed;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0,0,0,.55);
          z-index: 2147483647; /* 🚀 above everything */
        }
        .wal-modal {
          background: #fff;
          border-radius: 12px;
          width: min(92vw, 420px);
          padding: 20px 20px 14px;
          box-shadow: 0 20px 60px rgba(0,0,0,.4);
        }
        .wal-title { margin: 0 0 12px; }
        .wal-input {
          width: 100%;
          box-sizing: border-box;
          padding: 10px 12px;
          margin: 8px 0;
          border: 1px solid #d6d8dc;
          border-radius: 8px;
          font-size: 14px;
        }
        .wal-error { color: #d93025; margin: 6px 0 0; font-size: 13px; }
        .wal-actions { display: flex; gap: 8px; margin-top: 12px; }
        .wal-btn {
          border: 1px solid #d6d8dc;
          background: #fff;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
        }
        .wal-btn-primary {
          background: #4f46e5;
          border-color: #4f46e5;
          color: #fff;
        }
        .wal-btn:disabled,
        .wal-btn[disabled] { opacity: .6; cursor: not-allowed; }
        .wal-footer { margin-top: 10px; font-size: 13px; color:#555; }
        .wal-link {
          background: none; border: none; padding: 0;
          color: #2563eb; cursor: pointer; text-decoration: underline;
        }
      `}</style>
    </div>,
    document.body
  );
}